import React from "react";
import { AlertTriangle } from "lucide-react";

/**
 * Compact display of a shipment's "Stocking Location" (Storage_Detail).
 *
 *  - Renders nothing when the shipment is NOT at "Main yard" (per spec: hide entirely).
 *  - When the shipment IS at the yard but Storage_Detail is missing, renders a
 *    yellow "Stocking Location Needed" warning chip to flag historical data.
 *  - Otherwise, renders the location text in bold.
 *
 *  Variants:
 *    - `variant="cell"`   → table cell (default), no surrounding background
 *    - `variant="inline"` → for use inside a card, includes an "at" prefix
 */
export default function StockingLocationCell({ shipment, variant = "cell", testid }) {
  if (!shipment) return null;
  const atYard = shipment.Current_Location === "Main yard";
  if (!atYard) {
    if (variant === "cell") {
      return <span className="text-slate-400">—</span>;
    }
    return null;
  }
  const value =
    shipment.Storage_Detail === "Other Storage" && shipment.Storage_Other
      ? shipment.Storage_Other
      : shipment.Storage_Detail;

  if (!value) {
    return (
      <span
        data-testid={testid || `stocking-needed-${shipment.MR_Number || shipment.id}`}
        className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-900 border-2 border-amber-400 rounded-sm text-[10px] font-bold uppercase tracking-wider"
        title="Set a stocking location while the shipment is at the Main yard"
      >
        <AlertTriangle strokeWidth={2.5} className="h-3 w-3" />
        Stocking Location Needed
      </span>
    );
  }
  return (
    <span
      data-testid={testid || `stocking-value-${shipment.MR_Number || shipment.id}`}
      className="font-bold text-slate-800"
    >
      {value}
    </span>
  );
}
